import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, UserPlus, FileText, Scale, LogOut, Map, Briefcase, GraduationCap, MapPin, UserCog, Lock, X, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AccountSettingsModal from './AccountSettingsModal';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [isAccountModalOpen, setIsAccountModalOpen] = React.useState(false);

  useEffect(() => {
    // Close sidebar on navigation on mobile
    if (onClose) {
      onClose();
    }
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const getLinkClass = (path: string) => `nav-item ${isActive(path) ? 'active' : ''}`;

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      <aside className={`fixed left-0 top-0 h-full w-64 bg-judiciary-900 text-white shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-4 flex items-center justify-between border-b border-judiciary-700">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-full text-judiciary-800">
              <Scale size={24} />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-none">Judiciary HR</h1>
              <span className="text-xs text-judiciary-100 opacity-70">
                {user?.role === 'superadmin' ? 'Superadmin Panel' : (user?.role === 'admin' ? 'Admin Panel' : (user?.role === 'viewer' ? 'Demo Mode' : 'Operator Portal'))}
              </span>
            </div>
          </div>

          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
          <div className="space-y-0.5">
            <p className="px-4 text-xs font-semibold text-judiciary-400 uppercase tracking-wider mb-2">Main Menu</p>
            <Link to="/" className={getLinkClass('/')}>
              <LayoutDashboard size={20} /> <span>Advanced Dashboard</span>
            </Link>


            <Link to="/employees" className={getLinkClass('/employees')}>
              <Users size={20} /> <span>Employees List</span>
            </Link>

            {(user?.role !== 'viewer' || true) && (
              <Link to="/add-employee" className={getLinkClass('/add-employee')}>
                <UserPlus size={20} /> <span>New Registration</span>
              </Link>
            )}
          </div>

          {/* System Setup - Admin Only */}
          {(user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'viewer') && (
            <div className="mt-4 space-y-0.5">
              <p className="px-4 text-xs font-semibold text-judiciary-400 uppercase tracking-wider mb-2">System Setup</p>

              <Link to="/admin/locations" className={getLinkClass('/admin/locations')}>
                <Map size={20} /> <span>HQ & Tehsils</span>
              </Link>

              <Link to="/admin/posting" className={getLinkClass('/admin/posting')}>
                <MapPin size={20} /> <span>Posting Places</span>
              </Link>

              <Link to="/admin/designations" className={getLinkClass('/admin/designations')}>
                <Briefcase size={20} /> <span>Designations</span>
              </Link>

              <Link to="/admin/qualifications" className={getLinkClass('/admin/qualifications')}>
                <GraduationCap size={20} /> <span>Qualifications</span>
              </Link>

              <Link to="/admin/users" className={getLinkClass('/admin/users')}>
                <UserCog size={20} /> <span>User Management</span>
              </Link>
            </div>
          )}

          {/* Analysis & Reports - Admin and Viewer */}
          {(user?.role === 'admin' || user?.role === 'viewer' || user?.role === 'superadmin') && (
            <div className="mt-4 space-y-0.5">
              <p className="px-4 text-xs font-semibold text-judiciary-400 uppercase tracking-wider mb-2">Analysis</p>
              <Link to="/reports" className={getLinkClass('/reports')}>
                <FileText size={20} /> <span>Reports & Analysis</span>
              </Link>
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-judiciary-700 space-y-1">
          <button
            onClick={() => setIsAccountModalOpen(true)}
            className="flex items-center gap-3 px-3 py-2 text-judiciary-200 hover:text-white hover:bg-judiciary-800 transition-colors rounded-lg w-full text-left font-semibold text-sm"
          >
            <Settings size={18} />
            <span>Account Settings</span>
          </button>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 text-red-200 hover:text-white hover:bg-red-900/50 w-full transition-colors rounded-lg text-left font-semibold text-sm"
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>

        {user && (
          <AccountSettingsModal
            isOpen={isAccountModalOpen}
            onClose={() => setIsAccountModalOpen(false)}
            userId={user.id}
          />
        )}

        <style>{`
          .nav-item {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 8px 12px;
              border-radius: 8px;
              color: #dcfce7;
              transition: all 0.2s;
              text-decoration: none;
              font-weight: 600;
              font-size: 13px;
          }
          .nav-item:hover {
              background-color: #166534;
              color: white;
          }
          .nav-item.active {
              background-color: #16a34a;
              color: white;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(255,255,255,0.1);
            border-radius: 10px;
          }
        `}</style>
      </aside>
    </>
  );
};

export default Sidebar;